import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ApiError,
  useAccountInvitations,
  useAccountMembers,
  useAccounts,
  useAddAccountMember,
  useLeaveAccount,
  useRemoveAccountMember,
  useRevokeAccountInvitation,
  useResendAccountInvitation,
  useSeatCheckout,
} from '@wordpuppi/shared';
import { confirmDialog } from '@/lib/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { isAccountScopedQuery, useActiveAccount } from '@/lib/activeAccount';

export const Route = createFileRoute('/account/team')({
  component: TeamPage,
});

/** `error.details` of the 402 seat_limit_reached response (AB#331/#334). */
interface SeatLimitDetails {
  member_cap?: number;
  member_count?: number;
  seat_price?: string;
  checkout_url?: string;
}

/** Short absolute date, matching the app's existing member/date formatting. */
function formatDate(dateString?: string | null): string | null {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function TeamPage() {
  const { data: accountsData, isLoading: accountsLoading } = useAccounts();
  const accounts = accountsData?.data ?? [];

  // The account the menu switched to (AB#1014); owner-first until one is picked.
  // Picking here switches it everywhere.
  const { active, pickedPid, setActive } = useActiveAccount();
  const accountPid = active?.pid ?? '';
  const account = accounts.find((a) => a.pid === accountPid);
  const isOwner = account?.role === 'owner';

  const { data: membersData, isLoading: membersLoading } = useAccountMembers(accountPid);
  const members = membersData?.data ?? [];

  // Pending invitations only matter to owners (the endpoint is owner-only).
  const { data: invitationsData } = useAccountInvitations(isOwner ? accountPid : '');
  const invitations = invitationsData?.data ?? [];

  const addMember = useAddAccountMember();
  const removeMember = useRemoveAccountMember();
  const revokeInvitation = useRevokeAccountInvitation();
  const resendInvitation = useResendAccountInvitation();
  const seatCheckout = useSeatCheckout();
  const leaveAccount = useLeaveAccount();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [seatLimit, setSeatLimit] = useState<SeatLimitDetails | null>(null);

  const selectAccount = (pid: string) => {
    setActive(pid);
    setEmail('');
    setAddError(null);
    setSeatLimit(null);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !accountPid) return;
    setAddError(null);
    setSeatLimit(null);
    try {
      const res = await addMember.mutateAsync({ accountPid, email: trimmed });
      setEmail('');
      // AB#833: every add is an invitation — registered emails included.
      toast.success(`Invitation sent to ${res.data?.email ?? trimmed}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        // Fail-closed seat cap: turn the 402 into the buy-a-seat upsell.
        setSeatLimit((err.data.details ?? {}) as unknown as SeatLimitDetails);
      } else if (err instanceof ApiError && err.data.code === 'already_invited') {
        setAddError('An invite is already pending for that email.');
      } else if (err instanceof ApiError && err.data.code === 'already_member') {
        setAddError(`${trimmed} is already a member.`);
      } else {
        setAddError(err instanceof Error ? err.message : 'Could not add member');
      }
    }
  };

  const handleRevoke = async (invitationId: number, inviteEmail: string) => {
    try {
      await revokeInvitation.mutateAsync({ accountPid, invitationId });
      toast.success(`Invitation to ${inviteEmail} revoked`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke invitation');
    }
  };

  const handleResend = async (invitationId: number, inviteEmail: string) => {
    try {
      await resendInvitation.mutateAsync({ accountPid, invitationId });
      toast.success(`Invitation re-sent to ${inviteEmail}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not resend invitation');
    }
  };

  const handleBuySeat = async () => {
    if (!accountPid) return;
    try {
      const res = await seatCheckout.mutateAsync(accountPid);
      const url = res.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Seat checkout is not configured yet. Set the RevenueCat seat checkout URL.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start seat checkout');
    }
  };

  const handleRemove = async (userPid: string, label: string) => {
    if (!(await confirmDialog(`Remove ${label} from ${account?.name ?? 'this account'}?`, { destructive: true, confirmLabel: 'Remove member' }))) return;
    try {
      await removeMember.mutateAsync({ accountPid, userPid });
      setSeatLimit(null);
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove member');
    }
  };

  // AB#1018: a member walks away from someone else's account.
  const handleLeave = async () => {
    if (!account) return;
    const name = account.name;
    if (!(await confirmDialog(`Leave ${name}? You'll lose access to its sites.`, { destructive: true, confirmLabel: 'Leave account' }))) return;
    try {
      // Resolves only after the account list is refetched without it, so the
      // menu and pickers never offer the left account again.
      await leaveAccount.mutateAsync(account.pid);
      // Back to the user's own account (owner-first fallback). A pid change
      // makes main.tsx reset every account-scoped query; if nothing was picked
      // the pid won't change, so reset here to drop the left account's sites.
      if (pickedPid) setActive(null);
      else await queryClient.resetQueries({ predicate: isAccountScopedQuery });
      toast.success(`You left ${name}`);
      navigate({ to: '/sites' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not leave account');
    }
  };

  const seatPrice = seatLimit?.seat_price ?? '$1.99';

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Team</h1>
        <p className="text-sm text-muted-foreground">
          Everyone who can work on this account's sites.
        </p>
      </div>

      {accounts.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Account</span>
          <Select value={accountPid} onValueChange={selectAccount}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.pid} value={a.pid}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            {account
              ? `${account.member_count} of ${account.member_cap} seats used` +
                (account.extra_seats > 0
                  ? ` · ${account.extra_seats} extra ${account.extra_seats === 1 ? 'seat' : 'seats'}`
                  : '')
              : 'Members of your account.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accountsLoading || membersLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((m) => (
                <div
                  key={m.user_pid}
                  className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-3"
                >
                  <div className="space-y-0.5 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <span className="truncate">{m.name}</span>
                      {m.is_you && <Badge variant="secondary">You</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                    {formatDate(m.joined_at) && (
                      <p className="text-xs text-muted-foreground">
                        Joined {formatDate(m.joined_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      variant={m.role === 'owner' ? 'default' : 'secondary'}
                      className="uppercase tracking-wider"
                    >
                      {m.role}
                    </Badge>
                    {isOwner && m.role !== 'owner' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={removeMember.isPending}
                        onClick={() => handleRemove(m.user_pid, m.name || m.email)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {isOwner && (
            <div className="space-y-3 pt-2 border-t border-border">
              <form onSubmit={handleAdd} className="flex flex-wrap items-center gap-2 pt-4">
                <Input
                  type="email"
                  required
                  placeholder="teammate@example.com"
                  className="max-w-xs"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setAddError(null);
                  }}
                />
                <Button type="submit" disabled={addMember.isPending || !email.trim()}>
                  {addMember.isPending ? 'Adding...' : 'Add member'}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground">
                We'll email them an invitation to join — they become a member once they accept
                it.
              </p>
              {addError && <p className="text-sm text-destructive">{addError}</p>}
              {seatLimit && (
                <div className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">You've reached your seat limit</p>
                    <p className="text-xs text-muted-foreground">
                      This account includes {seatLimit.member_cap ?? account?.member_cap ?? 2}{' '}
                      members. Add a seat for {seatPrice}/mo to invite more.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    disabled={seatCheckout.isPending}
                    onClick={handleBuySeat}
                  >
                    {seatCheckout.isPending ? 'Starting...' : `Add a seat · ${seatPrice}/mo`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {!isOwner && account && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Only the account owner can add or remove members.
              </p>
              <Button
                variant="destructive"
                size="sm"
                disabled={leaveAccount.isPending}
                onClick={handleLeave}
              >
                {leaveAccount.isPending ? 'Leaving...' : 'Leave account'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isOwner && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending invitations</CardTitle>
            <CardDescription>
              People we've emailed an invite to who haven't joined yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {invitations.map((inv) => (
              <div
                key={inv.id}
                className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-3"
              >
                <div className="space-y-0.5 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(inv.expires_at)
                      ? `Expires ${formatDate(inv.expires_at)}`
                      : 'Pending'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="secondary" className="uppercase tracking-wider">
                    {inv.role}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={resendInvitation.isPending}
                    onClick={() => handleResend(inv.id, inv.email)}
                  >
                    Resend
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={revokeInvitation.isPending}
                    onClick={() => handleRevoke(inv.id, inv.email)}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}
